import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";

const tabItemVariants = cva(
  `inline-flex items-center justify-center gap-3 whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 relative cursor-pointer -mb-[1.5px]`,
  {
    variants: {
      variant: {
        bordered: "border-b-[1.5px]",
        borderless: "border-b-[1.5px]",
      },
      size: {
        small: "px-2 py-3 h-9",
        medium: "px-3 py-4 h-11",
        large: "px-3 py-4 h-12",
      },
      isSelected: {
        true: "",
        false: "",
      },
      isDisabled: {
        true: "cursor-not-allowed opacity-50",
        false: "",
      },
      isFullWidth: {
        true: "flex-1",
        false: "",
      },
    },
    compoundVariants: [
      // Bordered and borderless variants - not selected
      {
        variant: ["bordered", "borderless"],
        isSelected: false,
        isDisabled: false,
        class: `text-text-body-secondary 
          border-b-transparent
          text-action-ink-neutral-muted
          hover:text-action-ink-neutral-subtle
          hover:border-b-action-outline-neutral-faded-hover`,
      },
      // Bordered variant - selected
      {
        variant: "bordered",
        isSelected: true,
        isDisabled: false,
        class:
          "text-text-body-primary border-b-action-fill-primary-default text-action-ink-primary-normal hover:border-b-action-fill-primary-hover  z-10",
      },
      // Borderless variant - selected
      {
        variant: "borderless",
        isSelected: true,
        isDisabled: false,
        class:
          "text-text-body-primary border-b-action-fill-primary-default text-action-ink-primary-normal hover:border-b-action-fill-primary-hover z-10",
      },
      // Typography by size and selection
      {
        size: ["small", "medium"],
        isSelected: false,
        class: "text-body-medium-medium",
      },
      {
        size: ["small", "medium"],
        isSelected: true,
        class: "text-body-medium-semibold",
      },
      {
        size: "large",
        isSelected: false,
        class: "text-body-large-medium",
      },
      {
        size: "large",
        isSelected: true,
        class: "text-body-large-semibold",
      },
      // Bordered and borderless variants - disabled
      {
        variant: ["bordered", "borderless"],
        isDisabled: true,
        class: "text-text-body-disabled border-b-transparent",
      },
    ],
    defaultVariants: {
      variant: "bordered",
      size: "large",
      isSelected: false,
      isDisabled: false,
      isFullWidth: false,
    },
  }
);

export interface TabItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /**
   * Leading component (e.g., icon)
   */
  leadingComponent?: React.ReactNode;
  /**
   * Tab title/label
   */
  title: string;
  /**
   * Trailing component (e.g., badge, close button)
   */
  trailingComponent?: React.ReactNode;
  /**
   * Whether the tab is currently selected
   */
  isSelected?: boolean;
  /**
   * Whether the tab is disabled
   */
  isDisabled?: boolean;
  /**
   * Tab size variant
   */
  size?: "small" | "medium" | "large";
  /**
   * Tab visual variant
   */
  variant?: "bordered" | "borderless";
  /**
   * Whether the tab should take full width
   */
  isFullWidth?: boolean;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const TabItem = React.forwardRef<HTMLButtonElement, TabItemProps>(
  (
    {
      leadingComponent,
      title,
      trailingComponent,
      isSelected = false,
      isDisabled = false,
      size = "large",
      variant = "bordered",
      isFullWidth = false,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={isSelected}
        disabled={isDisabled}
        className={cn(
          tabItemVariants({
            variant,
            size,
            isSelected,
            isDisabled,
            isFullWidth,
          }),
          className
        )}
        onClick={onClick}
        {...props}
      >
        {leadingComponent && (
          <span className="inline-flex items-center">{leadingComponent}</span>
        )}
        <span className="inline-flex items-center">{title}</span>
        {trailingComponent && (
          <span className="inline-flex items-center">{trailingComponent}</span>
        )}
      </button>
    );
  }
);

TabItem.displayName = "TabItem";

export { TabItem, tabItemVariants };
